import React, { useContext, useMemo } from "react";
import { IconShieldCheck } from "@tabler/icons-react";
import BasicDataCollectionForm from "../../../components/basic-data-collection-form";
import { DataContext } from "../../../context/DataContext";

// Runs the datamatch (AgeMatch) verification with the data extracted by
// the BlinkID document scan. The extracted fields are already merged
// into the context `data` object by the Dcams page before this mounts,
// so the form prefills from `data` the same way every other
// data-collection flow does.
//
// Driven by the `datamatch` object on the dcams/blinkid route step:
//   { active, service, review, edit }
// - review=true  → show the populated form for the user to confirm
//                  (fields locked unless edit=true)
// - review=false → submit automatically; the processing screen shows
//                  from the first frame and status polling advances the
//                  journey exactly like a manual submission.
//
// `onFallback` (from the route step's `fallback` setting) fires when
// the datamatch fails with no attempts left — the Dcams page then
// switches to ID document picture verification instead of ending the
// journey with a FAIL.
export default function BlinkIdDatamatch({ datamatch, onFallback }) {
  const { text } = useContext(DataContext);

  const review = datamatch?.review === true;
  const editable = datamatch?.edit === true;

  // Same field set + validations as the AgeMatch page, minus the
  // route-inputs lookup — datamatch always carries name, address, dob.
  const formConfig = useMemo(
    () => ({
      fields: [
        {
          name: "fn",
          label: text?.general?.firstName || "First Name",
          group: "name",
          autoComplete: "given-name",
          enterKeyHint: "next",
          validation: (value) =>
            value.length < 2
              ? text?.general?.firstNameError || "First name is too short"
              : null,
        },
        {
          name: "ln",
          label: text?.general?.lastName || "Last Name",
          group: "name",
          autoComplete: "family-name",
          enterKeyHint: "next",
          validation: (value) =>
            value.length < 2
              ? text?.general?.lastNameError || "Last name is too short"
              : null,
        },
        {
          name: "address",
          label: text?.general?.address || "Address",
          type: "address",
          stacked: true,
          enterKeyHint: "next",
          validation: (value) =>
            value.addr && value.city && value.state && value.zip.trim().length === 5
              ? null
              : "Address is required",
        },
        {
          name: "dob",
          label: text?.general?.DateOfBirth || "Date of Birth",
          type: "date",
          validation: (value) => {
            const dobPattern =
              /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/;
            return dobPattern.test(value)
              ? null
              : text?.general?.DateOfBirthError || "Invalid Date of Birth";
          },
        },
      ],
    }),
    [text]
  );

  return (
    <BasicDataCollectionForm
      config={formConfig}
      title={text?.agematch?.title || "Verify your Age"}
      subtitle={
        review
          ? text?.agematch?.reviewSubtitle ||
            "Confirm the details from your ID before we verify your age."
          : undefined
      }
      buttonText={text?.agematch?.button || "Verify My Age"}
      icon={<IconShieldCheck size={40} stroke={1.5} />}
      componentKey="agematch/process"
      endpoint="/v3/stable/api/frontend/agematch"
      reAttempts={datamatch?.reattempts || 1}
      // Always suppress the built-in initial-load auto-submit: it only
      // POSTs in edge mode (non-edge just polls). review=false instead
      // submits via autoSubmit → submitHandler, which sends a real
      // request in both edge and non-edge sessions.
      skipInitialAutoSubmit={true}
      autoSubmit={!review}
      // Lock every field whenever the user isn't allowed to type —
      // confirm-only review mode, and auto-submit mode as defense in
      // depth (suppressRetryForm below should keep the form from ever
      // rendering there).
      readOnly={!review || !editable}
      // When the user can't enter data, a retryable failure (backend
      // WAITING / FAIL with attempts left) must NOT re-surface the
      // data-collection screen — the payload came off the barcode and
      // can't change. Finalize as FAIL instead, which routes into the
      // fallback (onTerminalFail) when the step opts in, or ends the
      // journey like any other failed verification.
      suppressRetryForm={!review || !editable}
      onTerminalFail={onFallback}
    />
  );
}
